import { jsx as _jsx } from "react/jsx-runtime";
import { cva } from 'class-variance-authority';
import { cn } from '../lib/cn';
/**
 * The handoff's own `Badge`, at the `sm` size the consoles use.
 *
 * Its metrics are the design system's, not shadcn's: `borderRadius: var(--radius-pill)` (a pill,
 * not a 12px rectangle), `fontSize: var(--text-2xs)` (11px), `fontFamily: var(--font-brand)`,
 * `fontWeight: var(--weight-semibold)`, `letterSpacing: 0.02em`, `padding: 2px 8px`, `gap: 5`,
 * and **no border** — every tone is a soft fill carrying its own text colour. This component
 * arrived as the shadcn default and had drifted a half-step off all six, which showed on every
 * badge in both consoles rather than on one screen.
 *
 * `outline` is the exception and draws its own border, because an outline badge is nothing
 * without one. It has no counterpart in the handoff; the internal console's queue page uses it
 * for a mono uppercase code.
 */
const badgeVariants = cva('inline-flex items-center gap-[5px] rounded-pill px-2 py-0.5 font-brand text-2xs font-semibold tracking-[0.02em] transition-colors', {
    variants: {
        variant: {
            default: 'bg-primary text-primary-foreground',
            secondary: 'bg-secondary text-secondary-foreground',
            // Soft, like every other tone. The handoff's Badge defines `danger.soft` as
            // `["var(--danger-bg)", "var(--danger)"]` and the consoles ask for the default
            // `variant="soft"` on every badge they draw — BETA-07's `refund` row included.
            // This was the one tone still rendering the shadcn solid: a saturated crimson
            // pill with white text where the design draws a pale tint with crimson text,
            // which is a different weight of statement on a screen where a refund is
            // ordinary rather than alarming.
            destructive: 'bg-destructive-subtle text-destructive',
            outline: 'border border-border text-foreground',
            // The handoff's own Badge carries a purple `soft` tone (`--purple-50` on `--purple-600`)
            // that this component was missing. The organizer console's roster marks an Owner with it.
            brand: 'bg-brand-50 text-brand-600',
            // `--success-bg` / `--warning-bg`, which are tokens in their own right rather than
            // the status colour at an opacity: `bg-success/15` is a tint of #1f8a5b and
            // `--success-bg` is #e4f4ec, and they are not the same green.
            success: 'bg-success-subtle text-success',
            // Was stock Tailwind amber, which is the one colour on either console that traced
            // to nothing in the brand guide — and it carried a `dark:` pair for a dark mode
            // neither console has.
            warning: 'bg-warning-subtle text-warning',
            muted: 'bg-muted text-muted-foreground',
        },
    },
    defaultVariants: { variant: 'default' },
});
export function Badge({ className, variant, ...props }) {
    return _jsx("span", { className: cn(badgeVariants({ variant }), className), ...props });
}
//# sourceMappingURL=badge.js.map